import { envEnum } from '@/types/env.enum';
import Cookies from 'js-cookie';

const APP_ENV = process.env.NEXT_PUBLIC_ENV || envEnum.development;
const API_URL = process.env.NEXT_PUBLIC_API_DOMAIN;

/**
 * Manages the CA_AUTH_TOKEN and CA_API_URL cookies, which will be used by non-CA frontends.
 * Ultimately, those non-CA frontends will need to communicate with the CA backend.
 */
export class CookieManager {
  static isLocalDevEnv(apiUrl: string | undefined = API_URL): boolean {
    return Boolean(apiUrl?.includes('localhost'));
  }

  private static getDomain(
    appEnv: envEnum | string = APP_ENV,
    apiUrl: string | undefined = API_URL,
  ): string | undefined {
    if (this.isLocalDevEnv(apiUrl)) {
      return undefined;
    }

    if (([envEnum.development] as Array<envEnum | string>).includes(appEnv)) {
      return '.nabis.dev';
    }

    // production / stage
    return '.nabis.com';
  }

  public static getAttributes(
    appEnv: envEnum | string = APP_ENV,
    apiUrl: string | undefined = API_URL,
  ): Cookies.CookieAttributes {
    return {
      // whether the cookie should be sent over HTTPS only
      secure: this.isLocalDevEnv(apiUrl) ? undefined : true,
      // the domain the cookie is available to
      domain: this.getDomain(appEnv),
      // whether the cookie should be accessible via client-side javascript
      httpOnly: false,
      /**
       * doesn't really matter since cookie is being utilized client-side across multiple subdomains,
       * but it's good practice to set it to strict anyway
       */
      sameSite: 'strict',
    };
  }

  private static getEnvSuffix(appEnv: envEnum | string = APP_ENV): string {
    return `-${appEnv}`;
  }

  private static getName(
    name: string,
    appEnv: envEnum | string = APP_ENV,
    apiUrl: string | undefined = API_URL,
  ): string {
    const SECURE_COOKIE_PREFIX_REMOTE_ONLY = '__Secure-';

    const envSuffix = this.getEnvSuffix(appEnv);

    const fullName = `${name}${envSuffix}`;

    if (this.isLocalDevEnv(apiUrl)) {
      return fullName;
    }

    return `${SECURE_COOKIE_PREFIX_REMOTE_ONLY}${fullName}`;
  }

  public static getV2AuthTokenName(appEnv: envEnum | string = APP_ENV, apiUrl: string | undefined = API_URL): string {
    return this.getName('V2_AUTH_TOKEN', appEnv, apiUrl);
  }

  public static getV2RefreshTokenName(
    appEnv: envEnum | string = APP_ENV,
    apiUrl: string | undefined = API_URL,
  ): string {
    return this.getName('V2_REFRESH_TOKEN', appEnv, apiUrl);
  }

  public static getCaAuthTokenName(appEnv: envEnum | string = APP_ENV, apiUrl: string | undefined = API_URL): string {
    return this.getName('CA_AUTH_TOKEN', appEnv, apiUrl);
  }

  public static getCaApiUrlName(appEnv: envEnum | string = APP_ENV, apiUrl: string | undefined = API_URL): string {
    return this.getName('CA_API_URL', appEnv, apiUrl);
  }

  public static removeCaCookies(appEnv: envEnum | string = APP_ENV, apiUrl: string | undefined = API_URL): void {
    Cookies.remove(this.getCaAuthTokenName(appEnv, apiUrl), this.getAttributes(appEnv));
    Cookies.remove(this.getCaApiUrlName(appEnv, apiUrl), this.getAttributes(appEnv));
  }
}
